import React, { useState, useEffect } from 'react';
import { Alert, CircularProgress, Link, TableFooter, Box, TableRow, Table, TableHead, TableBody, TableCell, Divider, Grid2 as Grid, Typography, FormControl, InputLabel, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails, TableContainer } from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BallotQuestionReport from '../../../types/reports/ballotQuestionReport';
import { useErrorBoundary } from 'react-error-boundary';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';


const BallotQuestionReports = (): JSX.Element => {

  document.title = 'OCPF - Ballot Question Committee Reports';

  const [ballotQuestions, setBallotQuestions] = useState<BallotQuestionReport[]>([]);
  const { showBoundary } = useErrorBoundary();
  const [isLoading, setIsLoading] = useState(true);
  const dropDownYears = Array.from(new Array(10), (val, index) => new Date().getFullYear() % 2 === 0 ? new Date().getFullYear() : new Date().getFullYear() - 1 - index * 2);
  const [reportYear, setReportYear] = useState(() => {
    if (new Date().getFullYear() % 2 === 0) {
      return new Date().getMonth() === 0 ? new Date().getFullYear() - 2 : new Date().getFullYear();
    }

    return new Date().getFullYear() - 1;
  });

  const updateReporYear = (event: any) => {
    setReportYear(event.target.value);
  };


  const getBallotQuestions = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/ballotQuestions/byQuestion/${reportYear}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get ballot question reports.'));
          }
          return result.json();
        })
        .then(result => {
          setBallotQuestions(result);
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
      showBoundary(new Error(`${error}`));
    }
  }

  useEffect(() => {
    getBallotQuestions();
  }, [reportYear]);


  //temp data
  // const ballotQuestionData = [{
  //   id: 39,
  //   ballotYear: 2024,
  //   description: "Question 1: State Auditor's Authority to Audit the Legislature",
  //   summaryUrl: "#",
  //   filers: [
  //     {
  //       ballotQuestionId: 39,
  //       position: "Support",
  //       cpfId: 95510,
  //       committeeName: "Committee for Transparent Democracy",
  //       reports: [
  //         {
  //           reportYear: '2024', 
  //           startBalance: '$46,551.28	',
  //           receiptItemizedTotal: "$0.00",
  //           expenditureItemizedTotal: "$0.00",
  //           endBalance: "$46,551.28",
  //           inkindItemizedTotal: "$0.00",
  //         },
  //       ]
  //     },
  //     {
  //       ballotQuestionId: 39,
  //       position: "Support",
  //       cpfId: 95524,
  //       committeeName: "Transparency Now",
  //       reports: []
  //     }
  //   ]
  // }];



  const renderTable = (filer: any) => {
    return (
      <TableContainer key={filer.cpfId + '-table-container'}>
        <Table>
          <TableHead>
            <TableRow key={filer.cpfId + '-header'}>
              <TableCell align='left' colSpan={3}>
                <Link sx={{ textDecoration: 'none', color: '#5d7da8'}} href={'/filers?q=' + filer.cpfId } target="_blank">
                  <Typography key={filer.cpfId}><b>{filer.committeeName + ' ' + filer.cpfId}</b></Typography>
                </Link>
              </TableCell>
              <TableCell align='right' colSpan={3}>
                <Typography key={filer.cpfId + '-url'}>
                  <Link sx={{ textDecoration: 'none', color: '#5d7da8'}} href={'/filers?q=' + filer.cpfId + '&section=Reports'} target="_blank">Committee Reports</Link>
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableHead>
            <TableRow key={filer.cpfId + '-table-header'}>
              <TableCell>Year</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>Receipts</TableCell>
              <TableCell>Expenditures</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Inkinds</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filer.reports.map((report: any, index: number) => (
              <TableRow key={filer.cpfId + '-report-' + index}>
                <TableCell>{report.reportYear}</TableCell>
                <TableCell>{report.startBalance}</TableCell>
                <TableCell>{report.receiptsYtd}</TableCell>
                <TableCell>{report.expendituresYtd}</TableCell>
                <TableCell>{report.currentCashOnHand}</TableCell>
                <TableCell>{report.inkindsYtd}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow key={filer.cpfId + '-footer'}>
              <TableCell>Support Total:</TableCell>
              <TableCell></TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(filer.reports.reduce((accumulator: number, current: any) => accumulator + current.receiptsYtdNumeric, 0))
                  }
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(filer.reports.reduce((accumulator: number, current: any) => accumulator + current.expendituresYtdNumeric, 0))
                  }
                </Typography>
              </TableCell>
              <TableCell></TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(filer.reports.reduce((accumulator: number, current: any) => accumulator + current.inkindsYtdNumeric, 0))
                  }
                </Typography>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Main>
      <Container>
      <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          marginBottom={2}
        >
          Ballot Question Committee Reports
        </Typography>
        <Divider />
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={12}>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <InputLabel>Year</InputLabel>
              <Select
                sx={{ fontSize: 'smaller' }}
                label="Report Year"
                value={reportYear}
                onChange={updateReporYear}
              >
                {
                  dropDownYears.map((year) =>
                    <MenuItem value={year} key={year} >{year}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12} marginTop={3}>

            {ballotQuestions.map((ballotQuestion: BallotQuestionReport) => (
              <Accordion key={ballotQuestion.id}>
                <AccordionSummary key={ballotQuestion.id + '-summary'}
                  expandIcon={<ArrowDropDownIcon />}
                  aria-controls="panel2-content"
                  id="panel2-header"
                >
                  <Typography variant="h6" component="h1">{ballotQuestion.description}</Typography>
                </AccordionSummary>
                <AccordionDetails key={ballotQuestion.id + '-details'}>
                  {
                    ballotQuestion.supportingCommittees && ballotQuestion.supportingCommittees.length > 0 &&
                    <>
                      <Alert variant="outlined" 
                        component={'h1'}
                        icon={false}
                        severity="info"
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          margin: '0 auto'  // This centers the Alert horizontally if it's smaller than its container
                        }}
                      >
                        <Typography variant="h6">Support</Typography>
                      </Alert>

                      <Box key={ballotQuestion.id + '-support-table-container'} sx={{ m: 2, marginBottom: 4 }}>
                        {ballotQuestion.supportingCommittees.map((filer: any) => (
                          renderTable(filer)
                        ))}
                      </Box>
                    </>
                  }

                  {ballotQuestion.opposingCommittees && ballotQuestion.opposingCommittees.length > 0 &&
                    <>
                      <Alert variant="outlined"  component={'h1'}
                        icon={false}
                        severity="warning"
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          margin: '0 auto'  // This centers the Alert horizontally if it's smaller than its container
                        }}
                      >
                        <Typography variant="h6">Oppose</Typography>
                      </Alert>

                      <Box key={ballotQuestion.id + '-oppose-table-container'} sx={{ m: 2, marginBottom: 4 }}>
                        {ballotQuestion.opposingCommittees.map((filer: any) => (
                          renderTable(filer)
                        ))}
                      </Box>
                    </>
                  }

                </AccordionDetails>
              </Accordion>
            ))}





          </Grid>
        </Grid>

         {/*loading indicator*/}
               <CircularProgress
                    size={50}
                    className={isLoading? "": "hidden"}
                    sx={{
                      position: "fixed",
                      left: "50%",
                      top: "20%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2
                     }}
                />
      </Container>
    </Main>
  );
};

export default BallotQuestionReports;
